import React, { useState, useEffect, useCallback, useRef } from 'react';
import { notificationAPI } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { useWebSocket } from '../../context/WebSocketContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Bell, MessageSquare, Calendar, Pill, Wifi } from 'lucide-react';
import { logger } from '../../lib/logger';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const isMountedRef = useRef(true);
  const wsNotificationCountRef = useRef(0);
  
  // WebSocket integration for real-time updates
  const { isConnected, notifications: wsNotifications, clearNotifications: clearWsNotifications } = useWebSocket();

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationAPI.getAll(false),
        notificationAPI.getCount(),
      ]);
      if (isMountedRef.current) {
        setNotifications(notifRes.data.slice(0, 10));
        setUnreadCount(countRes.data.count);
      }
    } catch (error) {
      logger.error('Failed to fetch notifications');
    }
  }, []);

  // Initial load and polling setup
  useEffect(() => {
    isMountedRef.current = true;
    
    // Use setTimeout to avoid synchronous setState in effect
    const timeoutId = setTimeout(() => {
      fetchNotifications();
    }, 0);
    
    // Poll every 30 seconds as fallback
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(timeoutId);
      clearInterval(interval);
    };
  }, [fetchNotifications]);

  // Refresh when WebSocket receives new notifications
  useEffect(() => {
    if (wsNotifications.length > wsNotificationCountRef.current) {
      wsNotificationCountRef.current = wsNotifications.length;
      // Use setTimeout to avoid synchronous setState
      const timeoutId = setTimeout(() => {
        fetchNotifications();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [wsNotifications.length, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markRead(notificationId);
      fetchNotifications();
    } catch (error) {
      logger.error('Failed to mark notification as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllRead();
      clearWsNotifications();
      wsNotificationCountRef.current = 0;
      fetchNotifications();
    } catch (error) {
      logger.error('Failed to mark all as read');
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'message':
        return <MessageSquare className="w-4 h-4 text-blue-500" />;
      case 'leave':
        return <Calendar className="w-4 h-4 text-violet-500" />;
      case 'approval':
        return <Pill className="w-4 h-4 text-amber-500" />;
      default:
        return <Bell className="w-4 h-4 text-slate-500" />;
    }
  };

  // Combine persistent notifications with WebSocket real-time count
  const totalUnread = unreadCount + wsNotifications.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={`relative w-16 h-16 rounded-full shadow-lg hover:shadow-xl transition-all bg-red-500 hover:bg-red-600 ${totalUnread > 0 ? 'animate-bounce' : ''}`}
          data-testid="notification-bell"
        >
          <Bell className="w-8 h-8 text-white" />
          {totalUnread > 0 && (
            <>
              <Badge className="absolute -top-2 -right-2 bg-white text-red-600 text-sm px-2 py-1 min-w-[28px] h-[28px] flex items-center justify-center font-bold border-2 border-red-500 animate-pulse">
                {totalUnread > 99 ? '99+' : totalUnread}
              </Badge>
              <span className="absolute inset-0 rounded-full bg-red-500 opacity-75 animate-ping"></span>
            </>
          )}
          {/* WebSocket connection indicator */}
          <div 
            className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white ${
              isConnected ? 'bg-emerald-500' : 'bg-slate-400'
            }`}
            title={isConnected ? 'Live updates active' : 'Offline'}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 mb-4" align="end" side="top">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">Notifications</h4>
            {isConnected && (
              <div className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                <Wifi className="w-3 h-3" />
                Live
              </div>
            )}
          </div>
          {totalUnread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAllAsRead} className="text-xs">
              Mark all read
            </Button>
          )}
        </div>
        
        {/* Real-time WebSocket notifications */}
        {wsNotifications.length > 0 && (
          <div className="border-b bg-blue-50">
            <div className="px-3 py-1.5 text-xs font-medium text-blue-700 border-b border-blue-100">
              Real-time Updates
            </div>
            {wsNotifications.slice(0, 5).map((notif, index) => (
              <div
                key={`ws-${notif.id || notif.timestamp || notif.title}`}
                className="p-3 border-b border-blue-100 hover:bg-blue-100/50"
              >
                <div className="flex gap-3">
                  <div className="mt-0.5">
                    <Wifi className="w-4 h-4 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{notif.title}</p>
                    <p className="text-xs text-slate-500 truncate">{notif.message}</p>
                  </div>
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        )}
        
        <div className="max-h-60 overflow-y-auto">
          {notifications.length === 0 && wsNotifications.length === 0 ? (
            <div className="p-4 text-center text-slate-500 text-sm">
              <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              No notifications
            </div>
          ) : (
            notifications.map((notif) => (
              <div
                key={notif.id}
                className={`p-3 border-b hover:bg-slate-50 cursor-pointer ${
                  !notif.is_read ? 'bg-blue-50/50' : ''
                }`}
                onClick={() => !notif.is_read && markAsRead(notif.id)}
              >
                <div className="flex gap-3">
                  <div className="mt-0.5">{getIcon(notif.notification_type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notif.is_read ? 'font-medium' : ''}`}>
                      {notif.title}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{notif.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{formatDateTime(notif.created_at)}</p>
                  </div>
                  {!notif.is_read && (
                    <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
